import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowRight, Lightbulb } from 'shared/icons';
import api from '../../../services/api';
import { MLB_URLS } from '../../../utils/ml';
import {
    FmtInt,
    FmtMoney,
    IsProductActive,
    LegacyEmptyState,
    LegacyFilterPills,
    LegacyLoading,
    LegacySearchBox,
    LegacyStatCard,
    Unwrap,
} from './legacyShared';

/**
 * Aba Legacy "Melhores Campanhas" — cópia de campaigns-v2.html (tab
 * suggestions). Cada card compara a campanha ATUAL com a MELHOR alternativa
 * (fórmula v2: líquido = consumer + rebate), com ganho líquido por venda e
 * economia para o consumidor.
 */
const LegacySuggestions = ({ includeInactive }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [rows, setRows] = useState([]);
    const [search, setSearch] = useState('');
    const [pillFilter, setPillFilter] = useState('all');

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const payload = Unwrap(await api.get('/legacy-campaigns/suggestions'));
            setRows(Array.isArray(payload?.rows) ? payload.rows : []);
        } catch (err) {
            setError(err.message || 'Erro ao carregar sugestões');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const visible = useMemo(
        () => (includeInactive ? rows : rows.filter((r) => IsProductActive(r.product_status))),
        [rows, includeInactive],
    );

    const stats = useMemo(() => {
        const unique = new Set(visible.map((r) => r.product_id)).size;
        const totalGain = visible.reduce((s, r) => s + parseFloat(r.net_gain || 0), 0);
        const totalSave = visible.reduce((s, r) => s + parseFloat(r.consumer_savings || 0), 0);
        const maxGain = visible.reduce((m, r) => Math.max(m, parseFloat(r.net_gain || 0)), 0);
        const typeCounts = {};
        for (const r of visible) typeCounts[r.alt_campaign_type] = (typeCounts[r.alt_campaign_type] || 0) + 1;
        const topType = Object.entries(typeCounts).sort((a, b) => b[1] - a[1])[0];
        return {
            unique,
            totalGain,
            totalSave,
            avgGain: unique > 0 ? totalGain / unique : 0,
            topType: topType ? topType[0] : '—',
            maxGain,
        };
    }, [visible]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return visible.filter((r) => {
            if (q) {
                const hay =
                    `${r.product_id} ${r.product_sku || ''} ${r.product_title || ''} ${r.current_campaign_name || ''} ${r.alt_campaign_name || ''}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            if (pillFilter === 'started' && r.alt_item_status !== 'started') return false;
            if (pillFilter === 'candidate' && r.alt_item_status !== 'candidate') return false;
            if (pillFilter === 'big-gain' && parseFloat(r.net_gain || 0) < 5) return false;
            return true;
        });
    }, [visible, search, pillFilter]);

    if (loading) return <LegacyLoading text="Carregando sugestões..." />;
    if (error) return <LegacyEmptyState icon={Lightbulb} text={`Erro: ${error}`} />;

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2.5 md:grid-cols-3 lg:grid-cols-6">
                <LegacyStatCard label="Itens c/ Sugestão" value={FmtInt(stats.unique)} />
                <LegacyStatCard label="Ganho Líquido / Venda" value={FmtMoney(stats.totalGain)} />
                <LegacyStatCard label="Economia Consumidor" value={FmtMoney(stats.totalSave)} />
                <LegacyStatCard label="Ganho Médio / Item"
                    value={stats.avgGain > 0 ? FmtMoney(stats.avgGain) : '—'}
                />
                <LegacyStatCard label="Top Tipo Sugerido" value={stats.topType} />
                <LegacyStatCard label="Maior Ganho Individual"
                    value={stats.maxGain > 0 ? FmtMoney(stats.maxGain) : '—'}
                />
            </div>

            <LegacySearchBox
                value={search}
                onChange={setSearch}
                placeholder="Buscar MLB, SKU, título ou campanha..."
            />
            <LegacyFilterPills
                pills={[
                    { id: 'all', label: 'Todas', count: visible.length },
                    {
                        id: 'started',
                        label: 'Already started',
                        count: visible.filter((r) => r.alt_item_status === 'started').length,
                    },
                    {
                        id: 'candidate',
                        label: 'Candidate',
                        count: visible.filter((r) => r.alt_item_status === 'candidate').length,
                    },
                    {
                        id: 'big-gain',
                        label: 'Ganho > R$ 5',
                        count: visible.filter((r) => parseFloat(r.net_gain || 0) >= 5).length,
                    },
                ]}
                active={pillFilter}
                onSelect={setPillFilter}
            />

            <p className="text-sm text-mono-500">
                <strong className="text-black dark:text-white">{FmtInt(filtered.length)}</strong> de{' '}
                {FmtInt(visible.length)} sugestões
            </p>

            {filtered.length === 0 ? (
                <LegacyEmptyState icon={Lightbulb} text="Nenhuma sugestão" />
            ) : (
                <div className="flex flex-col gap-2.5">
                    {filtered.map((r) => (
                        <div
                            key={`${r.product_id}-${r.alt_campaign_id}`}
                            className="rounded-xl border border-mono-200 bg-white p-3 transition-colors hover:border-mono-400 dark:border-mono-800 dark:bg-mono-950 dark:hover:border-mono-600"
                        >
                            {/* header do produto */}
                            <div className="mb-2.5 flex items-center gap-3 border-b border-mono-100 pb-2.5 dark:border-mono-900">
                                {r.product_thumbnail ? (
                                    <img
                                        src={r.product_thumbnail}
                                        alt=""
                                        className="h-12 w-12 rounded-lg border border-mono-200 object-cover dark:border-mono-800"
                                    />
                                ) : null}
                                <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <a
                                            href={MLB_URLS.search(r.product_id)}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="font-mono text-xs text-red-600 hover:underline dark:text-red-400"
                                        >
                                            {r.product_id}
                                        </a>
                                        {r.product_sku ? (
                                            <span className="rounded-md bg-mono-100 px-1.5 py-px text-xs text-mono-500 dark:bg-mono-900">
                                                {r.product_sku}
                                            </span>
                                        ) : null}
                                    </div>
                                    <p className="truncate text-[13px] text-black dark:text-white">
                                        {r.product_title || ''}
                                    </p>
                                </div>
                            </div>
                            {/* comparação atual → sugerida */}
                            <div className="grid grid-cols-1 items-stretch gap-2 md:grid-cols-[1fr_32px_1fr]">
                                <SugBox
                                    variant="current"
                                    title="Campanha atual"
                                    name={r.current_campaign_name || r.current_campaign_id}
                                    type={r.current_campaign_type}
                                    consumer={r.current_consumer_price}
                                    net={r.current_net_price}
                                    rebate={r.current_rebate_amount}
                                    meliPct={r.current_meli_pct}
                                />
                                <div className="hidden items-center justify-center text-green-500 md:flex">
                                    <ArrowRight size={22} />
                                </div>
                                <SugBox
                                    variant="suggested"
                                    title={`Sugestão (${r.alt_item_status || ''})`}
                                    name={r.alt_campaign_name || r.alt_campaign_id}
                                    type={r.alt_campaign_type}
                                    consumer={r.alt_consumer_price}
                                    net={r.alt_net_price}
                                    rebate={r.alt_rebate_amount}
                                    meliPct={r.alt_meli_pct}
                                />
                                <div className="grid grid-cols-2 gap-2 md:col-span-3">
                                    <div className="rounded-lg border border-mono-200 bg-mono-50 px-2.5 py-2 text-center dark:border-mono-800 dark:bg-mono-900">
                                        <p className="mb-0.5 text-[9px] uppercase text-mono-400">Ganho líquido/venda</p>
                                        <p className="text-base font-bold tabular-nums text-green-600 dark:text-green-400">
                                            +{FmtMoney(r.net_gain)}
                                        </p>
                                    </div>
                                    <div className="rounded-lg border border-mono-200 bg-mono-50 px-2.5 py-2 text-center dark:border-mono-800 dark:bg-mono-900">
                                        <p className="mb-0.5 text-[9px] uppercase text-mono-400">Economia consumidor</p>
                                        <p className="text-base font-bold tabular-nums text-blue-600 dark:text-blue-400">
                                            −{FmtMoney(r.consumer_savings)}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const SugBox = ({ variant, title, name, type, consumer, net, rebate, meliPct }) => (
    <div className="rounded-lg border border-mono-200 bg-mono-50 p-2.5 dark:border-mono-800 dark:bg-mono-900">
        <p
            className={`mb-1 text-[10px] font-semibold uppercase ${
                variant === 'current' ? 'text-orange-500' : 'text-green-600 dark:text-green-400'
            }`}
        >
            {title}
        </p>
        <p className="text-[13px] font-bold leading-tight text-black dark:text-white">{name}</p>
        <p className="mb-1.5 text-[10px] text-mono-400">{type || ''}</p>
        {[
            ['Consumer', FmtMoney(consumer), 'text-orange-500'],
            ['Líquido', FmtMoney(net), 'text-green-600 dark:text-green-400'],
            ['Rebate ML', FmtMoney(rebate), 'text-black dark:text-white'],
            ['Meli %', `${parseFloat(meliPct || 0).toFixed(1)}%`, 'text-black dark:text-white'],
        ].map(([label, value, cls]) => (
            <div key={label} className="flex justify-between py-0.5 text-xs tabular-nums">
                <span className="text-mono-400">{label}</span>
                <span className={`font-semibold ${cls}`}>{value}</span>
            </div>
        ))}
    </div>
);

export default LegacySuggestions;
